import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';

import { DialogClose } from '../../atoms/dialog-close';
import { ParentPaper } from '../../atoms/parent-paper';

import { SpotRegionSelect } from '../../organisms/spot/spot-region-select';
import { SpotPricePeriodSelect } from '../../organisms/spot/spot-price-period-select';
import { HDOSettingsUser } from '../../organisms/settings/hdo-settings-user';
import { SettingsSpotDistributionPrices } from '../settings/boxes/settings-spot-distribution-prices';
import { SettingsSpotOverflowPrice } from '../settings/boxes/settings-spot-overflow-price';
import { SettingsOverflowInfo } from '../settings/boxes/settings-overflow-info';
import { SettingsSpBatteryCharge } from '../settings/boxes/settings-sp-battery-charge';
import { SettingsSpBatteryDischargePrevent } from '../settings/boxes/settings-sp-battery-discharge-prevent';
import { SettingsSpOverflow } from '../settings/boxes/settings-sp-overflow';
import { SettingsSpBatteryToGrid } from '../settings/boxes/settings-sp-battery-to-grid';
import { SettingsBoilerSpotTemp } from '../settings/boxes/settings-boiler-spot-temp';
import { SettingsBoilerSpotBlocking } from '../settings/boxes/settings-boiler-spot-blocking';
import { SettingsHpSpotFlexibility } from '../settings/boxes/settings-hp-spot-flexibility';
import { useRive } from '@rive-app/react-webgl2';
import { FormattedStatus } from '../../store/formatted-status';

const thumbUpOrangeRiv = new URL(
  '../../static/animations/mr-battery-thumb-up/bright-orange.riv',
  import.meta.url
);

const spotWizardPathImage = new URL(
  '../../static/spot-wizard-path.gif',
  import.meta.url
);

export const useSpotWizardDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const wizardState = (location.state as any)?.spotWizard;
  const open = wizardState?.open === true;
  const currentStep = wizardState?.step ?? 0;

  const handleOpen = () => {
    navigate(location.pathname + location.search, {
      state: {
        ...location.state,
        spotWizard: { open: true, step: 0 },
      },
    });
  };

  const handleClose = () => {
    const newState = { ...location.state };
    delete newState.spotWizard;
    navigate(location.pathname + location.search, {
      state: newState,
    });
  };

  const setCurrentStep = (step: number) => {
    navigate(location.pathname + location.search, {
      state: {
        ...location.state,
        spotWizard: { open: true, step },
      },
    });
  };

  const dialog = (
    <SpotWizardDialog
      open={open}
      onClose={handleClose}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
    />
  );

  return {
    dialog,
    open: handleOpen,
    close: handleClose,
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
};

const SpotWizardDialog = ({
  open,
  onClose,
  currentStep,
  setCurrentStep,
}: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const prevPathnameRef = React.useRef(location.pathname);

  useEffect(() => {
    if (open && location.pathname !== prevPathnameRef.current) {
      onClose();
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, open, onClose]);

  const variables = useSelector((s) => s.variables);
  const activeSteps = WIZARD_STEPS.filter(
    (step) => !step.visible || step.visible(variables)
  );

  const currentIndex = Math.max(
    0,
    Math.min(currentStep, activeSteps.length - 1)
  );
  const totalActiveSteps = activeSteps.length;
  const isLastStep = currentIndex === totalActiveSteps - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentIndex + 1);
    } else {
      // Complete wizard
      dispatch(
        variablesChange({
          SPOT_WIZARD_COMPLETED_AT: new Date().toISOString(),
        })
      );
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentStep(currentIndex - 1);
    } else {
      onClose();
    }
  };

  const progress = ((currentIndex + 1) / totalActiveSteps) * 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'secondary.main',
          },
        },
      }}
    >
      <DialogClose onClose={onClose} />

      <DialogTitle
        sx={{
          textAlign: 'center',
          pt: 4,
          pb: 2,
        }}
      >
        <Typography variant="h3">
          <FormattedMessage
            id="spot_wizard.dialog.title"
            defaultMessage="Průvodce nastavením spotu"
          />
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
          <FormattedMessage
            id="spot_wizard.dialog.step"
            defaultMessage="Krok {current} z {total}"
            values={{ current: currentIndex + 1, total: totalActiveSteps }}
          />
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
      </Box>

      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 0,
          pt: 3,
        }}
      >
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1 }}>
            {activeSteps[currentIndex]?.render({ handleNext })}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              py: 2,
            }}
          >
            <Button onClick={handlePrev} variant="outlined">
              <FormattedMessage
                id="spot_wizard.dialog.prev"
                defaultMessage="Zpět"
              />
            </Button>
            <Button onClick={handleNext} variant="contained">
              {isLastStep ? (
                <FormattedMessage
                  id="spot_wizard.dialog.finish"
                  defaultMessage="Dokončit"
                />
              ) : (
                <FormattedMessage
                  id="spot_wizard.dialog.next"
                  defaultMessage="Další"
                />
              )}
            </Button>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

type StepProps = {
  handleNext: () => void;
};

type WizardStepDef = {
  render: (props: StepProps) => React.ReactNode;
  visible?: (variables: any) => boolean;
};

const WIZARD_STEPS: WizardStepDef[] = [
  { render: (props) => <Step1Info {...props} /> },
  { render: () => <Step2GeneralSettings /> },
  { render: () => <Step3DistributionPrices /> },
  { render: () => <Step4HDOTimes /> },
  {
    render: () => <Step5OverflowLimit />,
    visible: (v) => !!v.PV_OVERFLOW_CONTROL,
  },
  {
    render: () => <Step6BatteryPurchase />,
    visible: (v) =>
      !!(v.SP_BATTERY_CONTROL_CUSTOMER && v.SP_BATTERY_CONTROL_SERVICE),
  },
  { render: () => <Step7BatteryDischarge />, visible: (v) => !!v.PV_BATTERY },
  { render: () => <Step8PreferentialSale />, visible: (v) => !!v.PV_BATTERY },
  {
    render: () => <Step9EveningPeakDischarge />,
    visible: (v) => !!v.SP_DISCHARGE_TO_GRID_SER && !!v.PV_BATTERY,
  },
  { render: () => <Step10Boiler />, visible: (v) => !!v.HW_INSTALL },
  { render: () => <Step11HeatPump />, visible: (v) => !!v.HP_ENABLED },
  { render: () => <StepDone /> },
];

// Step 1: Info with buttons
const Step1Info = ({ handleNext }: StepProps) => {
  const [view, setView] = useState<'buttons' | 'help1' | 'help2'>('buttons');

  const { chargingButtonVisible, dischargingButtonVisible } = useSelector(
    ({
      variables: {
        SP_INSTALL,
        PV_BATTERY,
        SP_BATTERY_CONTROL_SERVICE,
        SP_DISCHARGE_TO_GRID_SER,
      },
    }) => ({
      chargingButtonVisible:
        SP_INSTALL && PV_BATTERY && SP_BATTERY_CONTROL_SERVICE,
      dischargingButtonVisible:
        SP_INSTALL && PV_BATTERY && SP_DISCHARGE_TO_GRID_SER,
    })
  );

  if (view === 'help1') {
    return (
      <Stack gap={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setView('buttons')} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h2">
            <FormattedMessage
              id="spot_wizard.info.help1.title"
              defaultMessage="Proč se mi dnes nenabila baterie ze spotu?"
            />
          </Typography>
        </Box>
        <ParentPaper>
          <Paper>
            <Typography variant="body1">
              <FormattedStatus type="spotBatteryChargeFromGrid" />
            </Typography>
          </Paper>
        </ParentPaper>
      </Stack>
    );
  }

  if (view === 'help2') {
    return (
      <Stack gap={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setView('buttons')} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h2">
            <FormattedMessage
              id="spot_wizard.info.help2.title"
              defaultMessage="Proč se mi dnes nevybíjela baterie do spotu?"
            />
          </Typography>
        </Box>
        <ParentPaper>
          <Paper>
            <Typography variant="body1">
              <FormattedStatus type="spotBatteryDischargeToGrid" />
            </Typography>
          </Paper>
        </ParentPaper>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.info.title"
          defaultMessage="Informace ke spotu"
        />
      </Typography>
      <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {chargingButtonVisible && (
          <Button
            variant="outlined"
            fullWidth
            sx={{ py: 2 }}
            onClick={() => setView('help1')}
          >
            <FormattedMessage
              id="spot_wizard.info.button1"
              defaultMessage="Proč se mi dnes nenabila baterie ze spotu?"
            />
          </Button>
        )}
        {dischargingButtonVisible && (
          <Button
            variant="outlined"
            fullWidth
            sx={{ py: 2 }}
            onClick={() => setView('help2')}
          >
            <FormattedMessage
              id="spot_wizard.info.button2"
              defaultMessage="Proč se mi dnes nevybíjela baterie do spotu?"
            />
          </Button>
        )}
        <Button
          variant="outlined"
          fullWidth
          sx={{ py: 2 }}
          onClick={handleNext}
        >
          <span>
            <FormattedMessage
              id="spot_wizard.info.button3"
              defaultMessage="Chci pomoc s nastavením"
            />
          </span>
        </Button>
      </Paper>
    </Stack>
  );
};

// Step 2: General Settings
const Step2GeneralSettings = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.general_settings.title"
          defaultMessage="Nejprve obecné nastavení"
        />
      </Typography>
      <ParentPaper>
        <SpotRegionSelect />
        <SpotPricePeriodSelect />
      </ParentPaper>
    </Stack>
  );
};

// Step 3: Distribution Prices
const Step3DistributionPrices = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.distribution_prices.title"
          defaultMessage="Zadejte cenu za distribuci v nízkém a vysokém tarifu"
        />
      </Typography>
      <SettingsSpotDistributionPrices />
    </Stack>
  );
};

// Step 4: HDO Times
const Step4HDOTimes = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.hdo_times.title"
          defaultMessage="Nastavení HDO časů určí období nízkého tarifu"
        />
      </Typography>
      <HDOSettingsUser />
    </Stack>
  );
};

// Step 5: Overflow Limit
const Step5OverflowLimit = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.overflow_limit.title"
          defaultMessage="Omezení přetoku do sítě"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.overflow_limit.paragraph1"
              defaultMessage="Pokud máte spot pouze pro nákup energie, můžete nastavit -10 Kč. Přetok je omezen na maximální velikost nastavenou servisním technikem."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.overflow_limit.paragraph2"
              defaultMessage="Pokud máte spot i pro prodej energie, je výhodné zastavit akumulaci při ceně, která odpovídá provizi Vašeho dodavatele. Provize bývá nejčastěji 0.30 - 0.70 Kč/kWh. "
            />
          </Typography>
        </Paper>
        <SettingsSpotOverflowPrice />
        <SettingsOverflowInfo />
      </ParentPaper>
    </Stack>
  );
};

// Step 6: Battery Purchase
const Step6BatteryPurchase = () => {
  const SP_USER_ADVANCED_CONTROL = useSelector(
    (s) => s.variables.SP_USER_ADVANCED_CONTROL
  );

  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.battery_purchase.title"
          defaultMessage="Nákup energie do baterie"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.battery_purchase.paragraph1"
              defaultMessage="Toto nastavení umožní při nízké výrobě elektrárny nabít baterii z nízkého spotu."
            />
          </Typography>
        </Paper>
        {SP_USER_ADVANCED_CONTROL && (
          <Paper>
            <Typography variant="body1">
              <FormattedMessage
                id="spot_wizard.battery_purchase.paragraph2"
                defaultMessage="Věnujte pozornost nastavení strategie. Pokud nastavíte 20%, Infigy bude spíše čekat na fotovoltaiku. Naopak při nastavení 70% bude spíše nakupovat na spotu. Doporučujeme nastavit 25%."
              />
            </Typography>
          </Paper>
        )}
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.battery_purchase.paragraph3"
              defaultMessage="Pokud máte pouze prodej energie na spotu, můžete nabíjení deaktivovat."
            />
          </Typography>
        </Paper>
        <SettingsSpBatteryCharge />
      </ParentPaper>
    </Stack>
  );
};

// Step 7: Battery Discharge
const Step7BatteryDischarge = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.battery_discharge.title"
          defaultMessage="Vybíjení baterie do vlastní spotřeby"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.battery_discharge.paragraph1"
              defaultMessage="Toto nastavení zakáže vybíjení baterie při nízké ceně spotu. Díky tomu si uchováte energii pro vyšší ceny dne."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.battery_discharge.paragraph2"
              defaultMessage="Pokud máte pouze prodej energie na spotu, můžete zákaz deaktivovat."
            />
          </Typography>
        </Paper>
        <SettingsSpBatteryDischargePrevent />
      </ParentPaper>
    </Stack>
  );
};

// Step 8: Preferential Sale
const Step8PreferentialSale = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.preferential_sale.title"
          defaultMessage="Přednostní prodej energie"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.preferential_sale.paragraph1"
              defaultMessage="Tato funkce umožní zákaz ukládání energie při ranní špičce. Dojde k deaktivaci nabíjení baterie, bojleru a tepelného čerpadla. Akumulace se aktivuje při nízké ceně nebo při prodání odhadovaného přebytku energie."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.preferential_sale.paragraph2"
              defaultMessage="Pokud máte pouze nákup energie na spotu, můžete funkci deaktivovat."
            />
          </Typography>
        </Paper>
        <SettingsSpOverflow />
      </ParentPaper>
    </Stack>
  );
};

// Step 9: Evening Peak Discharge
const Step9EveningPeakDischarge = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.evening_peak_discharge.title"
          defaultMessage="Vybíjení baterie při večerní špičce"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.evening_peak_discharge.paragraph1"
              defaultMessage="Infigy automaticky odhadne spotřebu energie do ranních hodin. Nadbytek může být prodán za nejvyšší cenu dne."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.evening_peak_discharge.paragraph2"
              defaultMessage="Pokud máte pouze nákup energie na spotu, můžete vybíjení deaktivovat."
            />
          </Typography>
        </Paper>
        <SettingsSpBatteryToGrid />
      </ParentPaper>
    </Stack>
  );
};

// Step 10: Boiler
const Step10Boiler = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.boiler.title"
          defaultMessage="Ohřev bojleru"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.boiler.paragraph1"
              defaultMessage="Toto nastavení umožní při nízké výrobě elektrárny ohřát bojler z nejlevnějších cen dne do nastavené teploty. Infigy stále udržuje základní teplotu a využívá navýšení teploty na teplotu ze spotu."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.boiler.paragraph2"
              defaultMessage="Zároveň je možné blokovat ohřev při střední a nebo vysoké ceně."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.boiler.paragraph3"
              defaultMessage="Pokud máte pouze prodej energie na spotu, můžete teplotu nastavit na 10°C a deaktivovat blokaci ohřevu."
            />
          </Typography>
        </Paper>
        <SettingsBoilerSpotTemp />
        <SettingsBoilerSpotBlocking />
      </ParentPaper>
    </Stack>
  );
};

// Step 11: Heat Pump
const Step11HeatPump = () => {
  return (
    <Stack gap={2}>
      <Typography variant="h2">
        <FormattedMessage
          id="spot_wizard.heat_pump.title"
          defaultMessage="Tepelné čerpadlo"
        />
      </Typography>
      <ParentPaper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.heat_pump.paragraph1"
              defaultMessage="Toto nastavení umožní při nízké výrobě elektrárny navyšovat výkon tepelného čerpadla při nízké ceně a naopak snižovat výkon při vysoké ceně."
            />
          </Typography>
        </Paper>
        <Paper>
          <Typography variant="body1">
            <FormattedMessage
              id="spot_wizard.heat_pump.paragraph2"
              defaultMessage="Pokud máte pouze prodej energie na spotu, odložení i výšení spotřeby deaktivovat."
            />
          </Typography>
        </Paper>
        <SettingsHpSpotFlexibility />
      </ParentPaper>
    </Stack>
  );
};

// Step Done
const StepDone = () => {
  const { RiveComponent, rive } = useRive({
    src: thumbUpOrangeRiv.href,
    animations: 'Timeline 1',
    autoplay: true,
  });

  useEffect(() => {
    if (!rive) return;
    const timer = setTimeout(() => {
      rive.pause();
    }, 4000);
    return () => clearTimeout(timer);
  }, [rive]);

  return (
    <Stack gap={2}>
      <Typography variant="h2" sx={{ textAlign: 'center' }}>
        <FormattedMessage id="spot_wizard.done.title" defaultMessage="Hotovo" />
      </Typography>
      <ParentPaper>
        <Paper>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body1" sx={{ flex: 1, maxWidth: 550 }}>
              <FormattedMessage
                id="spot_wizard.done.description"
                defaultMessage="Prošli jste nastavení spotu. Veškeré parametry lze upravit ve spodní části stránky Spot, kde můžete i opět vyvolat tohoto průvodce."
              />
            </Typography>
            <Box
              sx={{
                width: 100,
                height: 120,
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Box sx={{ inset: -20, position: 'absolute' }}>
                <RiveComponent />
              </Box>
            </Box>
          </Box>
        </Paper>
        <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
          <Box
            component="img"
            src={spotWizardPathImage.toString()}
            alt=""
            sx={{ width: 260, height: 'auto' }}
          />
        </Box>
      </ParentPaper>
    </Stack>
  );
};
